(() => {
  const main = document.querySelector("main[data-template='index']");
  const desktop = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!main || !main.querySelector('.trots-hero-carousel')) return;

  let animationFrame;
  let isAnimating = false;
  let wheelCooldownUntil = 0;
  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartScroll = 0;
  let touchTracking = false;
  let touchAllowsNativeScroll = false;
  let touchBlockedByAnimation = false;
  let queuedTouchDirection = 0;

  const sectionFor = (selector) => main.querySelector(selector)?.closest('.shopify-section');

  const getTargets = () => {
    const targets = [
      sectionFor('.trots-hero-carousel'),
      sectionFor('.trots-collection-overview'),
      sectionFor('.trots-feature-tiles'),
      main.querySelector(':scope > .shopify-section.ui-test-product-list'),
      desktop.matches ? null : sectionFor('.trots-retailer-logos'),
      sectionFor('.trots-heart-gallery'),
      sectionFor('.trots-newsletter-quiz'),
      sectionFor('.trots-trustpilot-reviews'),
      document.querySelector('body > footer'),
    ].filter(Boolean);

    return [...new Set(targets)];
  };

  const getHeaderOffset = () => {
    if (desktop.matches) return 0;

    return Math.round(document.querySelector('header')?.getBoundingClientRect().height || 0);
  };

  const getPositions = () =>
    getTargets().map((target, index) => {
      if (index === 0) return 0;

      return Math.max(
        0,
        Math.round(target.getBoundingClientRect().top + window.scrollY - getHeaderOffset())
      );
    });

  const isInterfaceActive = (target) => {
    if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"], dialog')) {
      return true;
    }

    return [...document.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"]')].some(
      (dialog) => dialog.getClientRects().length > 0
    );
  };

  const animateTo = (destination) => {
    window.cancelAnimationFrame(animationFrame);

    const start = window.scrollY;
    const distance = destination - start;
    const duration = desktop.matches
      ? Math.min(760, Math.max(560, Math.abs(distance) * 0.72))
      : Math.min(620, Math.max(420, Math.abs(distance) * 0.55));
    const startTime = performance.now();

    if (Math.abs(distance) < 2) return;

    isAnimating = true;

    const step = (time) => {
      const progress = Math.min((time - startTime) / duration, 1);
      const eased = progress < 0.5
        ? 16 * progress ** 5
        : 1 - (-2 * progress + 2) ** 5 / 2;

      window.scrollTo(0, start + distance * eased);

      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(step);
      } else {
        window.scrollTo(0, destination);
        isAnimating = false;

        if (queuedTouchDirection) {
          const direction = queuedTouchDirection;
          const nextDestination = findDestination(direction);

          queuedTouchDirection = 0;

          if (nextDestination !== undefined) {
            animationFrame = window.requestAnimationFrame(() => animateTo(nextDestination));
          }
        }
      }
    };

    animationFrame = window.requestAnimationFrame(step);
  };

  const findDestination = (direction) => {
    const positions = getPositions();
    const current = window.scrollY;
    const tolerance = 12;

    if (direction > 0) {
      return positions.find((position) => position > current + tolerance);
    }

    return [...positions].reverse().find((position) => position < current - tolerance);
  };

  const getCurrentTargetState = (current = window.scrollY) => {
    const targets = getTargets();
    const positions = getPositions();
    const tolerance = 12;
    const currentIndex = positions.reduce(
      (found, position, index) => (position <= current + tolerance ? index : found),
      0
    );
    const currentTarget = targets[currentIndex];
    const visibleHeight = window.innerHeight - getHeaderOffset();
    const targetHeight = currentTarget?.getBoundingClientRect().height || 0;
    const targetStart = positions[currentIndex];
    const targetEnd = Math.max(targetStart, targetStart + targetHeight - visibleHeight);

    return {
      currentIndex,
      isLong: targetHeight > visibleHeight + tolerance,
      positions,
      targetEnd,
      targetStart,
    };
  };

  const canScrollInsideCurrentTarget = (direction, current = window.scrollY) => {
    if (desktop.matches) return false;

    const { isLong, targetEnd, targetStart } = getCurrentTargetState(current);
    const tolerance = 12;

    if (!isLong) return false;

    return direction > 0
      ? current < targetEnd - tolerance
      : current > targetStart + tolerance;
  };

  const handleWheel = (event) => {
    if (!desktop.matches || reducedMotion.matches || event.ctrlKey || isInterfaceActive(event.target)) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) || Math.abs(event.deltaY) < 4) return;

    if (isAnimating || performance.now() < wheelCooldownUntil) {
      event.preventDefault();
      return;
    }

    if (canScrollInsideCurrentTarget(Math.sign(event.deltaY))) return;

    const destination = findDestination(Math.sign(event.deltaY));
    if (destination === undefined) return;

    event.preventDefault();
    wheelCooldownUntil = performance.now() + 420;
    animateTo(destination);
  };

  const handleKeydown = (event) => {
    if (!desktop.matches || reducedMotion.matches || isInterfaceActive(event.target)) return;

    const direction = ['PageDown', 'ArrowDown'].includes(event.key)
      ? 1
      : ['PageUp', 'ArrowUp'].includes(event.key)
        ? -1
        : 0;

    if (!direction || isAnimating) return;

    const destination = findDestination(direction);
    if (destination === undefined) return;

    event.preventDefault();
    animateTo(destination);
  };

  const handleTouchStart = (event) => {
    touchTracking = false;
    touchAllowsNativeScroll = false;
    touchBlockedByAnimation = false;

    // Native touch scrolling is intentionally retained on tablet and mobile.
    if (!desktop.matches || reducedMotion.matches || event.touches.length !== 1) return;
    if (isInterfaceActive(event.target)) return;

    touchStartX = event.touches[0].clientX;
    touchStartY = event.touches[0].clientY;
    touchStartScroll = window.scrollY;

    // Keep a second, rapid swipe from competing with the active snap animation.
    if (isAnimating) {
      touchBlockedByAnimation = true;
      return;
    }

    touchTracking = true;
  };

  const handleTouchMove = (event) => {
    if (touchBlockedByAnimation || isAnimating) {
      event.preventDefault();
      return;
    }

    if (!touchTracking || desktop.matches || reducedMotion.matches || event.touches.length !== 1) return;

    const deltaX = touchStartX - event.touches[0].clientX;
    const deltaY = touchStartY - event.touches[0].clientY;

    if (Math.abs(deltaY) < 8 || Math.abs(deltaY) <= Math.abs(deltaX) * 1.15) return;

    touchAllowsNativeScroll = canScrollInsideCurrentTarget(Math.sign(deltaY), touchStartScroll);

    // Short sections snap via JavaScript. Long sections retain natural scrolling
    // until their top or bottom edge has been reached.
    if (!touchAllowsNativeScroll) event.preventDefault();
  };

  const handleTouchEnd = (event) => {
    if (touchBlockedByAnimation) {
      if (event.changedTouches.length !== 1) {
        touchBlockedByAnimation = false;
        return;
      }

      const deltaX = touchStartX - event.changedTouches[0].clientX;
      const deltaY = touchStartY - event.changedTouches[0].clientY;

      touchBlockedByAnimation = false;

      if (Math.abs(deltaY) >= 42 && Math.abs(deltaY) > Math.abs(deltaX) * 1.15) {
        queuedTouchDirection = Math.sign(deltaY);

        if (!isAnimating) {
          const destination = findDestination(queuedTouchDirection);

          queuedTouchDirection = 0;
          if (destination !== undefined) animateTo(destination);
        }
      }

      return;
    }

    if (!touchTracking || desktop.matches || reducedMotion.matches || isAnimating || event.changedTouches.length !== 1) return;

    touchTracking = false;

    const deltaX = touchStartX - event.changedTouches[0].clientX;
    const deltaY = touchStartY - event.changedTouches[0].clientY;

    if (Math.abs(deltaY) < 42 || Math.abs(deltaY) <= Math.abs(deltaX) * 1.15) return;

    const direction = Math.sign(deltaY);
    const state = getCurrentTargetState(touchStartScroll);

    if (touchAllowsNativeScroll && state.isLong) {
      const current = window.scrollY;
      const boundary = direction > 0 ? state.targetEnd : state.targetStart;

      if (Math.abs(current - boundary) <= 28 || (direction > 0 ? current > boundary : current < boundary)) {
        animateTo(boundary);
      }

      return;
    }

    const destination = state.positions[state.currentIndex + direction];

    if (destination === undefined) return;

    wheelCooldownUntil = performance.now() + 360;
    animateTo(destination);
  };

  window.addEventListener('wheel', handleWheel, { passive: false });
  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('touchstart', handleTouchStart, { passive: true });
  window.addEventListener('touchmove', handleTouchMove, { passive: false });
  window.addEventListener('touchend', handleTouchEnd, { passive: true });

  document.addEventListener('shopify:section:load', () => {
    window.cancelAnimationFrame(animationFrame);
    isAnimating = false;
    queuedTouchDirection = 0;
    touchBlockedByAnimation = false;
  });
})();
